<template>
  <!-- Nothing is drawn until the look is known, so a switched-on look never
       flashes the old card first. A look that cannot be read is the old one. -->
  <v-app v-if="look === null" class="login-wait" />

  <!-- The hooldusplaat look (Süsteem → Seaded → Intra sisselogimine), the same
       design as the customer portal's login. -->
  <v-app v-else-if="look.plate" class="plate">
    <div class="plate-login">
      <main class="plate-login__main">
        <div class="plate-login__intro">
          <img :src="logo" alt="Gaspre" class="plate-login__logo" width="88" height="99">
          <h1 class="plate-wide">Gaspre Intra</h1>
          <p>Kõik asjad ühes kohas.</p>
        </div>
        <form class="plate-login__form" novalidate @submit.prevent="login">
          <p v-if="error" class="plate-error" role="alert">{{ error.message }}</p>
          <!-- Pääsuvõtmed (Süsteem → Turvalisus): first, the password under it. -->
          <template v-if="passkeyOn">
            <button class="plate-btn plate-btn--key" type="button" :disabled="loading" @click="passkeyLogin">
              {{ loading === 'key' ? 'Ootan seadet…' : 'Logi sisse pääsuvõtmega' }}
            </button>
            <p class="plate-or"><span>või parooliga</span></p>
          </template>
          <div class="plate-field">
            <!-- "Kasutaja", not "E-post": the page need not tell a stranger what
                 the account name looks like. type="text" for the same reason. -->
            <label for="username">Kasutaja</label>
            <input id="username" v-model="form.username" name="username" type="text" autocomplete="username" autocapitalize="none" spellcheck="false">
          </div>
          <div class="plate-field">
            <label for="password">Parool</label>
            <div class="plate-pass">
              <input
                id="password"
                v-model="form.password"
                name="password"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="current-password"
              >
              <button type="button" :aria-pressed="showPassword" @click="showPassword = !showPassword">
                {{ showPassword ? 'Peida' : 'Näita' }}
              </button>
            </div>
          </div>
          <button class="plate-btn" type="submit" :disabled="loading">
            {{ loading === true ? 'Login sisse…' : 'Logi sisse' }}
          </button>
        </form>
        <p class="plate-other">Oled klient? <a href="/customer/login">Kliendiportaal →</a></p>
      </main>
      <!-- The office's own words (Süsteem → Seaded); empty means no footer. -->
      <footer v-if="footer" class="plate-login__foot"><p>{{ footer }}</p></footer>
    </div>
  </v-app>

  <v-app v-else id="inspire">
    <v-main>
      <v-container class="fill-height" fluid>
        <v-row align="center" justify="center">
          <v-col cols="12" sm="8" md="4">
            <v-card class="elevation-12">
              <v-toolbar color="primary" theme="dark" flat>
                <v-toolbar-title>Gaspre Intra</v-toolbar-title>
                <v-spacer></v-spacer>
              </v-toolbar>
              <v-card-text>
                <v-alert v-if="error" type="error">
                  {{error.message}}
                </v-alert>
                <v-btn v-if="passkeyOn" block color="primary" class="mb-4" :loading="loading === 'key'" prepend-icon="mdi-fingerprint" @click="passkeyLogin">Logi sisse pääsuvõtmega</v-btn>
                <v-form>
                  <v-text-field
                    v-model="form.username"
                    label="Kasutaja"
                    name="username"
                    prepend-icon="mdi-account"
                    type="text"
                    v-on:keyup.enter="login"
                  />
                  <v-text-field
                    v-model="form.password"
                    label="Parool"
                    name="password"
                    id="password"
                    prepend-icon="mdi-lock"
                    type="password"
                    v-on:keyup.enter="login"
                  />
                </v-form>
              </v-card-text>
              <v-card-actions>
                <v-btn variant="text" href="/customer/login" color="green">Kliendikeskkond</v-btn>
                <v-spacer></v-spacer>
                <v-btn :loading="loading === true" @click="login" color="primary">Logi sisse</v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import api from './../api'
import { mapActions } from 'vuex'
import logo from '@/assets/gaspre-logo.png'
import loadArchivo from '@/plugins/archivo'
import { config as passkeyConfig, login as loginWithPasskey, supported as passkeySupported } from '@/plugins/passkey'

// How long the page waits for the look before drawing the old one anyway.
const LOOK_TIMEOUT_MS = 3000

export default {
  data () {
    return {
      logo,
      look: null,
      showPassword: false,
      loading: false,
      form: {
        username: '',
        password: ''
      },
      error: null,
      passkeyOn: false
    }
  },
  computed: {
    to () {
      const to = this.$route.query.to
      return to && to !== '/' ? to : { name: 'calendar' }
    },
    footer () {
      return ((this.look && this.look.loginFooter) || '').trim()
    }
  },
  async mounted () {
    // Asked alongside the look; a page drawn first gets the button a moment later.
    passkeyConfig().then(cfg => { this.passkeyOn = !!(cfg.user && cfg.user.on) && passkeySupported() })
    const fallback = { plate: false, loginFooter: '' }
    const timer = setTimeout(() => { if (this.look === null) this.look = fallback }, LOOK_TIMEOUT_MS)
    try {
      const look = await api.getIntraLook()
      if (this.look === null) this.look = { plate: !!look.plate, loginFooter: look.loginFooter || '' }
    } catch (err) {
      if (this.look === null) this.look = fallback
    } finally {
      clearTimeout(timer)
    }
    if (this.look.plate) loadArchivo()
  },
  methods: {
    ...mapActions(['isAuthenticated']),
    async passkeyLogin () {
      if (this.loading) return
      this.error = null
      this.loading = 'key'
      try {
        await loginWithPasskey('user')
        await this.isAuthenticated(true)
        this.$router.push(this.to).catch(() => {})
      } catch (err) {
        if (!err.cancelled) this.error = err
      } finally {
        this.loading = false
      }
    },
    async login () {
      if (this.loading) {
        return
      }
      try {
        this.error = null
        this.loading = true
        await api.login(this.form.username, this.form.password)
        await this.isAuthenticated(true)
        // The push rejects when a guard redirects it (the page in ?to= is not for
        // this user's roles, say). The login itself has succeeded by then.
        this.$router.push(this.to).catch(() => {})
      } catch (err) {
        this.error = err
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
/* The customer portal's hooldusplaat login (customer3/src/styles/plate.css),
   the same tokens and rules, scoped to this page. */
.login-wait { background: #F4F6F5; }
.plate {
  --p-ground: #F4F6F5;
  --p-surface: #FFFFFF;
  --p-ink: #16322A;
  --p-ink-2: #3E504A;
  --p-ink-3: #5B6B66;
  --p-line: #C9D2CE;
  --p-line-strong: #9AAAA4;
  --p-green: #2E7D32;
  --p-green-dark: #1B5E20;
  --p-green-bg: #EAF3EA;
  --p-flame: #1F5FD1;
  --p-amber-bg: #FFF4E0;
  --p-amber-ink: #5B3700;
  background: var(--p-ground);
  color: var(--p-ink);
  font-family: 'Archivo Variable', 'Archivo', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 16px;
  line-height: 1.45;
}
/* Doubled class: Vuetify colours every link in .v-application itself. */
.plate.v-application a { color: var(--p-green); font-weight: 600; }
.plate.v-application a:hover { color: var(--p-green-dark); }
.plate :focus-visible { outline: 3px solid var(--p-flame); outline-offset: 2px; }
.plate-wide { font-stretch: 125%; }
.plate-login { min-height: 100vh; min-height: 100dvh; display: flex; flex-direction: column; }
.plate-login__main {
  flex: 1; width: 100%; max-width: 440px; margin: 0 auto; padding: 48px 24px 24px;
  display: flex; flex-direction: column; gap: 26px;
}
.plate-login__intro { display: flex; flex-direction: column; gap: 18px; }
.plate-login__intro h1 { margin: 0; font-size: 34px; line-height: 1.05; font-weight: 700; letter-spacing: -.01em; text-wrap: balance; }
.plate-login__intro p { margin: 0; color: var(--p-ink-2); max-width: 32ch; }
.plate-login__logo { width: 88px; height: auto; display: block; }
.plate-login__form { display: flex; flex-direction: column; gap: 18px; margin: 0; }
.plate-login__foot { border-top: 2px solid var(--p-ink); background: var(--p-surface); padding: 16px 24px calc(18px + env(safe-area-inset-bottom, 0px)); }
/* 392 = the column's 440 less its own side padding, so the footer lines up with the form. */
.plate-login__foot p { margin: 0 auto; max-width: 392px; font-size: 15px; font-weight: 700; white-space: pre-line; }
.plate-field { display: flex; flex-direction: column; gap: 6px; }
.plate-field label { font-size: 15px; font-weight: 600; }
.plate-field input {
  min-height: 52px; padding: 0 14px; font: inherit; font-size: 18px; color: var(--p-ink); width: 100%;
  background: var(--p-surface); border: 1.5px solid var(--p-line-strong); border-radius: 6px;
}
.plate-field input:focus { border-color: var(--p-ink); outline: none; }
.plate-field input:focus-visible { outline: 3px solid var(--p-flame); outline-offset: 2px; }
.plate-pass { position: relative; display: flex; }
.plate-pass input { padding-right: 76px; }
.plate-pass button {
  position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
  min-height: 40px; padding: 0 10px; border: 0; background: none; border-radius: 4px;
  font: inherit; font-size: 14px; font-weight: 600; color: var(--p-green); cursor: pointer;
}
.plate-pass button:hover { background: var(--p-green-bg); }
.plate-error { margin: 0; padding: 12px 14px; border-radius: 6px; background: var(--p-amber-bg); color: var(--p-amber-ink); font-weight: 600; font-size: 15px; }
.plate-btn {
  min-height: 54px; padding: 0 16px; border-radius: 6px; border: 0; width: 100%;
  font: inherit; font-weight: 700; font-size: 17px; cursor: pointer;
  background: var(--p-green); color: #fff;
}
.plate-btn:hover { background: var(--p-green-dark); }
.plate-or { margin: 0; display: flex; align-items: center; gap: 10px; font-size: 14px; color: var(--p-ink-3); }
.plate-or::before, .plate-or::after { content: ""; flex: 1; height: 1px; background: var(--p-line); }
.plate-btn:disabled { opacity: .7; cursor: default; }
.plate-other { margin: 0; padding-top: 16px; border-top: 1px solid var(--p-line); font-size: 15px; color: var(--p-ink-2); }
</style>
